import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../contexts/ChatContext';
import { friendshipApi } from '../services/friendshipApi';
import Avatar from './Avatar';
import { IoClose, IoSearch, IoPeople, IoPerson, IoCheckmark } from 'react-icons/io5';
import './NewConversationModal.css';

const NewConversationModal = ({ onClose, onCreated }) => {
  const { t } = useTranslation();
  const { createConversation } = useChat();
  const [friends, setFriends] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [isGroupMode, setIsGroupMode] = useState(false);
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [groupName, setGroupName] = useState('');

  useEffect(() => {
    loadFriends();
  }, []);

  const loadFriends = async () => {
    try {
      const response = await friendshipApi.getFriends();
      if (response.success) {
        setFriends(response.data || []);
      }
    } catch (error) {
      console.error('Failed to load friends:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredFriends = friends.filter(friend =>
    (friend.display_name || friend.username || '')
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  const handleSelectFriend = (friend) => {
    if (isGroupMode) {
      setSelectedFriends(prev => {
        const isSelected = prev.some(f => f.id === friend.id);
        if (isSelected) {
          return prev.filter(f => f.id !== friend.id);
        }
        return [...prev, friend];
      });
    } else {
      handleCreateConversation([friend.id], false, '');
    }
  };

  const handleCreateGroup = async () => {
    if (selectedFriends.length < 2 || !groupName.trim()) return;
    setCreating(true);
    try {
      const participantIds = selectedFriends.map(f => f.id);
      await createConversation(participantIds, true, groupName.trim());
      onClose();
    } catch (error) {
      console.error('Failed to create group:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleCreateConversation = async (participantIds, isGroup, name) => {
    setCreating(true);
    try {
      const conv = await createConversation(participantIds, isGroup, name);
      onCreated(conv);
    } catch (error) {
      console.error('Failed to create conversation:', error);
    } finally {
      setCreating(false);
    }
  };

  // Close on backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="new-conversation-modal">
        <div className="modal-header">
          <h3>{t('chat.newConversation')}</h3>
          <button className="modal-close" onClick={onClose}>
            <IoClose />
          </button>
        </div>

        {/* Mode toggle */}
        <div className="modal-mode-toggle">
          <button
            className={`mode-btn ${!isGroupMode ? 'active' : ''}`}
            onClick={() => { setIsGroupMode(false); setSelectedFriends([]); }}
          >
            <IoPerson /> {t('chat.newConversation') || 'New Chat'}
          </button>
          <button
            className={`mode-btn ${isGroupMode ? 'active' : ''}`}
            onClick={() => setIsGroupMode(true)}
          >
            <IoPeople /> {t('chat.createGroup') || 'Create Group'}
          </button>
        </div>

        {/* Group name input (only in group mode) */}
        {isGroupMode && (
          <div className="modal-group-name">
            <input
              type="text"
              placeholder={t('chat.groupName') || 'Group name'}
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
            />
          </div>
        )}

        {/* Search */}
        <div className="modal-search">
          <IoSearch className="search-icon" />
          <input
            type="text"
            placeholder={isGroupMode ? (t('chat.addParticipants') || 'Add participants...') : (t('chat.searchFriends') || 'Search friends...')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>

        {/* Selected friends count in group mode */}
        {isGroupMode && selectedFriends.length > 0 && (
          <div className="modal-selected-count">
            {selectedFriends.length} {t('chat.selected') || 'selected'}
          </div>
        )}

        <div className="modal-friends-list">
          {loading ? (
            <div className="modal-loading"><div className="loading-spinner"></div></div>
          ) : filteredFriends.length === 0 ? (
            <div className="modal-empty">
              <p>{search ? t('chat.noResults') : t('chat.noFriends')}</p>
            </div>
          ) : (
            filteredFriends.map(friend => {
              const isSelected = selectedFriends.some(f => f.id === friend.id);
              return (
                <div
                  key={friend.id}
                  className={`friend-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleSelectFriend(friend)}
                >
                  <Avatar user={friend} size="medium" />
                  <div className="friend-info">
                    <span className="friend-name">{friend.display_name}</span>
                    <span className="friend-username">@{friend.username}</span>
                  </div>
                  {isGroupMode && isSelected && (
                    <div className="friend-check">
                      <IoCheckmark />
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Create group button */}
        {isGroupMode && (
          <div className="modal-actions">
            <button
              className="create-group-btn"
              onClick={handleCreateGroup}
              disabled={creating || selectedFriends.length < 2 || !groupName.trim()}
            >
              {creating ? t('common.loading') : (t('chat.createGroup') || 'Create Group')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewConversationModal;
